import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { AppDispatch } from "_shared/_stores/store-types";
import { calendarEventListTag, calendarEventsApi } from "calendar/_api/calendar-event-api";
import Guid from "_shared/guids/guid";
import Session from "users/__session/session";
import {
  removeWorkAgreementApiCaches,
  upsertWorkAgreementApiCaches,
} from "work/work-agreements/_api/work-agreement-api";
import { validateJSON, WorkAgreementJSON } from "work/work-agreements/_schemas/work-agreement-schema";
import { removeWorkAgreement, updateWorkAgreement } from "work/work-agreements/_store/work-agreement-redux-slice";

const updateWorkAgreementByIdCache = ({
  dispatch,
  getState,
  session,
  workAgreementJSON,
}: {
  dispatch: AppDispatch;
  getState: () => RootState;
  session: Session;
  workAgreementJSON: WorkAgreementJSON;
}) => {
  if (!workAgreementJSON.id) {
    throw new Error("Received work agreement event with no work agreement ID");
  }

  upsertWorkAgreementApiCaches({ dispatch, getState, session, workAgreementJSON });
  dispatch(updateWorkAgreement(workAgreementJSON));
};

const getWorkAgreementIdValue = (payload: unknown): string | undefined => {
  if (typeof payload === "string") return payload;
  if (payload && typeof payload === "object" && "id" in payload) {
    const id = (payload as { id?: unknown }).id;
    return typeof id === "string" ? id : undefined;
  }

  return undefined;
};

const isUnauthorizedPayload = (payload: unknown): payload is { id: string; isUnauthorized: true } => {
  if (!payload || typeof payload !== "object") return false;
  return (payload as { isUnauthorized?: unknown }).isUnauthorized === true;
};

const handleWorkAgreementRemoval = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(calendarEventsApi.util.invalidateTags([calendarEventListTag]));
  const workAgreementIdValue = getWorkAgreementIdValue(payload);
  if (!workAgreementIdValue) {
    throw new Error("Received work-agreement-deleted event with no work agreement ID");
  }

  const session = Session.loadFromStorage(() => {});
  removeWorkAgreementApiCaches({ dispatch, getState, session, workAgreementIdValue });
  dispatch(removeWorkAgreement(new Guid(workAgreementIdValue)));
};

const handleWorkAgreementUpsert = (payload: unknown) => (dispatch: AppDispatch, getState: () => RootState) => {
  if (isUnauthorizedPayload(payload)) {
    handleWorkAgreementRemoval(payload)(dispatch, getState);
    return;
  }

  dispatch(calendarEventsApi.util.invalidateTags([calendarEventListTag]));
  const workAgreementJSON = validateJSON(payload);
  const session = Session.loadFromStorage(() => {});
  updateWorkAgreementByIdCache({ dispatch, getState, session, workAgreementJSON });
};

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add("work-agreement-created", handleWorkAgreementUpsert)
  .add("work-agreement-updated", handleWorkAgreementUpsert)
  .add("work-agreement-deleted", handleWorkAgreementRemoval);

export const workAgreementsAblyReduxMiddleware = reduxAblyMiddleware<RootState, AppDispatch>({
  callbacks,
  channelName: ablyChannelNamesForSession.workAgreements,
  connectionHealth: { key: "workAgreements", label: "Work Agreements" },
  shouldConnectionStartImmediately: true,
});

